'use client';

import Link from 'next/link';
import type { ReactNode } from 'react';

import { trackNavClick, type NavSurface } from '@/app/lib/trackNav';

/**
 * A `next/link` that reports which control opened a tool.
 *
 * WHY A WRAPPER. The homepage (`app/page.tsx`) and `RelatedTools.tsx` are SERVER components, so
 * they cannot carry an `onClick`. Marking either `'use client'` to add one would be a much larger
 * change than the measurement justifies — the homepage is the site's highest-authority page and is
 * deliberately server-rendered. This keeps the client boundary to one tiny leaf.
 *
 * ⚠️ The anchor is still server-rendered into the HTML: a client component's markup is produced
 * during SSR, so crawlers see a real `<a href>` and the internal linking that the SEO work depends
 * on is unaffected. Only the click handler needs JavaScript, and if it never loads the navigation
 * still works — it simply goes unreported, which is the correct way round.
 */
export default function TrackedToolLink({
  slug,
  surface,
  className,
  children,
}: {
  /** Bare slug, e.g. 'banner-editor' — reported as-is so it joins on the same key `tool_view` uses. */
  slug: string;
  surface: NavSurface;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Link
      href={`/tools/${slug}`}
      className={className}
      onClick={() => trackNavClick(slug, surface)}
    >
      {children}
    </Link>
  );
}
